import React, { useMemo } from 'react';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { calculateOperationsSovScore, DEFAULT_SOV_WEIGHTS } from '../../utils/sovScoreCalculations';
import type { OperationsAnswers } from '../../types/workplace';

interface OperationsScoringStepProps {
  answers: OperationsAnswers;
  onAnswer: (questionId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => void;
}

interface QuestionOption {
  sealValue: number;
  labelDe: string;
  labelEn: string;
}

interface Question {
  id: 'q1' | 'q2' | 'q3' | 'q4';
  titleDe: string;
  titleEn: string;
  placeholderDe: string;
  placeholderEn: string;
  options: QuestionOption[];
}

const OPERATIONS_QUESTIONS: Question[] = [
  {
    id: 'q1',
    titleDe: 'Wer betreibt die Infrastruktur?',
    titleEn: 'Who operates the infrastructure?',
    placeholderDe: 'Bitte auswählen…',
    placeholderEn: 'Please select…',
    options: [
      { sealValue: 4, labelDe: 'Internes EU-Team (eigene Mitarbeitende, EU-Standort)', labelEn: 'Internal EU team (own employees, EU location)' },
      { sealValue: 3, labelDe: 'EU-Dienstleister (externer Betrieb, EU-Recht, EU-Standort)', labelEn: 'EU service provider (external ops, EU law, EU location)' },
      { sealValue: 2, labelDe: 'Gemischt (EU und Non-EU Anteile)', labelEn: 'Mixed (EU and non-EU components)' },
      { sealValue: 1, labelDe: 'Non-EU-Dienstleister (außerhalb EU-Jurisdiktion)', labelEn: 'Non-EU service provider (outside EU jurisdiction)' },
      { sealValue: 0, labelDe: 'Unbekannt / nicht definiert', labelEn: 'Unknown / not defined' },
    ],
  },
  {
    id: 'q2',
    titleDe: 'Wo sitzt der Support (1st/2nd/3rd Level)?',
    titleEn: 'Where is support located (1st/2nd/3rd level)?',
    placeholderDe: 'Bitte auswählen…',
    placeholderEn: 'Please select…',
    options: [
      { sealValue: 4, labelDe: 'Ausschließlich EU (alle Support-Stufen unter EU-Recht)', labelEn: 'Exclusively EU (all support levels under EU law)' },
      { sealValue: 3, labelDe: 'Überwiegend EU (>80% EU, Rest vertraglich geregelt)', labelEn: 'Predominantly EU (>80% EU, rest contractually regulated)' },
      { sealValue: 2, labelDe: 'Gemischt (EU und Non-EU ohne klare Regelung)', labelEn: 'Mixed (EU and non-EU without clear regulation)' },
      { sealValue: 1, labelDe: 'Überwiegend Non-EU', labelEn: 'Predominantly non-EU' },
      { sealValue: 0, labelDe: 'Nicht definiert', labelEn: 'Not defined' },
    ],
  },
  {
    id: 'q3',
    titleDe: 'Sind SLAs und Verträge ausschließlich unter EU-Recht abgeschlossen?',
    titleEn: 'Are SLAs and contracts exclusively under EU law?',
    placeholderDe: 'Bitte auswählen…',
    placeholderEn: 'Please select…',
    options: [
      { sealValue: 4, labelDe: 'Ja, vollständig — EU-Recht, EU-Gerichtsstand, DSGVO-konform', labelEn: 'Yes, fully — EU law, EU jurisdiction, GDPR compliant' },
      { sealValue: 3, labelDe: 'Überwiegend — EU-Recht mit einigen nicht-EU Klauseln', labelEn: 'Predominantly — EU law with some non-EU clauses' },
      { sealValue: 2, labelDe: 'Teilweise — gemischte Rechtswahl', labelEn: 'Partially — mixed choice of law' },
      { sealValue: 1, labelDe: 'Nein — non-EU Recht dominant', labelEn: 'No — non-EU law dominant' },
      { sealValue: 0, labelDe: 'Keine formalen SLAs vorhanden', labelEn: 'No formal SLAs in place' },
    ],
  },
  {
    id: 'q4',
    titleDe: 'Wie ist die Exit-Fähigkeit / Portabilität?',
    titleEn: 'What is the exit capability / portability?',
    placeholderDe: 'Bitte auswählen…',
    placeholderEn: 'Please select…',
    options: [
      { sealValue: 4, labelDe: 'Vollständig dokumentiert — Datenexport, Migrationspfad, kein Lock-in', labelEn: 'Fully documented — data export, migration path, no lock-in' },
      { sealValue: 3, labelDe: 'Weitgehend — Export möglich, leichter Aufwand', labelEn: 'Largely — export possible, minor effort' },
      { sealValue: 2, labelDe: 'Teilweise — Export möglich aber aufwendig', labelEn: 'Partially — export possible but complex' },
      { sealValue: 1, labelDe: 'Schwierig — proprietäre Formate, hoher Lock-in', labelEn: 'Difficult — proprietary formats, high lock-in' },
      { sealValue: 0, labelDe: 'Kein Exit-Konzept vorhanden', labelEn: 'No exit concept in place' },
    ],
  },
];

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: `1px solid ${theme.border}`,
  borderRadius: 4, fontSize: 13, background: '#fff',
};

export const OperationsScoringStep: React.FC<OperationsScoringStepProps> = ({ answers, onAnswer }) => {
  const { language } = useLanguage();
  const isDE = language === 'de';

  const allAnswered = answers.q1 !== null && answers.q2 !== null && answers.q3 !== null && answers.q4 !== null;

  const computedScore = useMemo(() => {
    if (!allAnswered) return null;
    return calculateOperationsSovScore(answers, DEFAULT_SOV_WEIGHTS);
  }, [answers, allAnswered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {OPERATIONS_QUESTIONS.map((q) => {
        const selected = answers[q.id];
        return (
          <div key={q.id}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
              {isDE ? q.titleDe : q.titleEn}
            </div>
            <select
              value={selected !== null ? String(selected) : ''}
              onChange={e => {
                const val = e.target.value;
                if (val !== '') onAnswer(q.id, Number(val));
              }}
              style={selectStyle}
            >
              <option value="">{isDE ? q.placeholderDe : q.placeholderEn}</option>
              {q.options.map(opt => (
                <option key={opt.sealValue} value={String(opt.sealValue)}>
                  {isDE ? opt.labelDe : opt.labelEn}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      {/* Score display when all answered */}
      {allAnswered && computedScore?.score !== null && computedScore !== null && (
        <div style={{ background: theme.cardBg, borderRadius: 8, padding: 12, textAlign: 'center', border: `1px solid ${theme.border}`, marginTop: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: theme.muted }}>
            {isDE ? 'Betriebs- und Supportmodell Score' : 'Operations & Support Model Score'}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: theme.secondary }}>
            {computedScore!.score!.toFixed(1)}%
          </p>
        </div>
      )}
    </div>
  );
};
